/**
 * useLocation — GPS location sampling and averaging with outlier filtering.
 * 
 * Features:
 * - Multi-sample GPS capture during scanning (every 5 seconds)
 * - Outlier filtering using Haversine distance from median
 * - Single-shot location capture for manual "Use Current Location" buttons
 * - Permission request handling
 */

import { useState, useCallback, useRef, useEffect } from 'react'

interface LocationSample {
  lat: number
  lng: number
  accuracy: number
  timestamp: number
}

interface AverageLocation {
  lat: number
  lng: number
  accuracy: number
  sampleCount: number
}

type PermissionStatus = 'unknown' | 'requesting' | 'granted' | 'denied'

const SAMPLE_INTERVAL_MS = 5000 // Sample every 5 seconds
const MAX_OUTLIER_DISTANCE_METERS = 500 // Reject samples >500m from median

/**
 * Haversine formula for distance between two GPS coordinates in meters.
 */
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3 // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((lng2 - lng1) * Math.PI) / 180

  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculate median of an array of numbers.
 */
function median(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

/**
 * Filter outliers and calculate average location.
 */
function calculateAverage(samples: LocationSample[]): AverageLocation | null {
  if (samples.length === 0) return null

  // Single sample — no filtering needed
  if (samples.length === 1) {
    return {
      lat: samples[0].lat,
      lng: samples[0].lng,
      accuracy: samples[0].accuracy,
      sampleCount: 1,
    }
  }

  // Find median point
  const lats = samples.map((s) => s.lat)
  const lngs = samples.map((s) => s.lng)
  const medianLat = median(lats)
  const medianLng = median(lngs)

  // Filter: reject samples >500m from median
  const filtered = samples.filter((s) => {
    const dist = haversineDistance(s.lat, s.lng, medianLat, medianLng)
    return dist <= MAX_OUTLIER_DISTANCE_METERS
  })

  if (filtered.length === 0) {
    // All samples were outliers — fall back to median
    console.warn('⚠️ [useLocation] All samples rejected as outliers, using median')
    return {
      lat: medianLat,
      lng: medianLng,
      accuracy: 50, // Default accuracy when all samples rejected
      sampleCount: samples.length,
    }
  }

  // Calculate average of filtered samples
  const avgLat = filtered.reduce((sum, s) => sum + s.lat, 0) / filtered.length
  const avgLng = filtered.reduce((sum, s) => sum + s.lng, 0) / filtered.length
  const avgAccuracy = filtered.reduce((sum, s) => sum + s.accuracy, 0) / filtered.length

  console.log(
    `📍 [useLocation] Averaged ${filtered.length}/${samples.length} samples (${samples.length - filtered.length} outliers removed)`
  )

  return {
    lat: avgLat,
    lng: avgLng,
    accuracy: avgAccuracy,
    sampleCount: filtered.length,
  }
}

export function useLocation() {
  const [samples, setSamples] = useState<LocationSample[]>([])
  const [averageLocation, setAverageLocation] = useState<AverageLocation | null>(null)
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('unknown')
  const [error, setError] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(false)
  /** Single-shot location captured early (e.g. on page load) */
  const [initialLocation, setInitialLocation] = useState<{ lat: number; lng: number; accuracy: number } | null>(null)
  const [isLocating, setIsLocating] = useState(false)

  const intervalRef = useRef<number | null>(null)

  // Request GPS permission and get initial sample
  const requestPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by this browser')
      setPermissionStatus('denied')
      return false
    }

    setPermissionStatus('requesting')
    setError(null)

    return new Promise<boolean>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (_position) => {
          setPermissionStatus('granted')
          console.log('✓ [useLocation] GPS permission granted')
          resolve(true)
        },
        (err) => {
          setPermissionStatus('denied')
          setError(`Location permission denied: ${err.message}`)
          console.error('✗ [useLocation] GPS permission denied:', err)
          resolve(false)
        },
        { enableHighAccuracy: true, timeout: 10000 }
      )
    })
  }, [])

  // Take a single GPS sample
  const takeSample = useCallback(() => {
    if (!navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const sample: LocationSample = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now(),
        }
        setSamples((prev) => [...prev, sample])
        console.log(`📍 [useLocation] Sample captured: ${sample.lat.toFixed(6)}, ${sample.lng.toFixed(6)} (±${Math.round(sample.accuracy)}m)`)
      },
      (err) => {
        console.warn('⚠️ [useLocation] Sample failed:', err.message)
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    )
  }, [])

  // Start continuous sampling
  const startSampling = useCallback(async () => {
    if (isActive) return

    // Request permission first
    const granted = await requestPermission()
    if (!granted) return

    setIsActive(true)
    setError(null)

    // Take first sample immediately
    takeSample()

    // Then sample every SAMPLE_INTERVAL_MS
    intervalRef.current = window.setInterval(() => {
      takeSample()
    }, SAMPLE_INTERVAL_MS)

    console.log(`📍 [useLocation] Started sampling (every ${SAMPLE_INTERVAL_MS / 1000}s)`)
  }, [isActive, requestPermission, takeSample])

  // Stop sampling and calculate average
  const stopSampling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsActive(false)

    // Calculate average from all samples
    if (samples.length > 0) {
      const avg = calculateAverage(samples)
      setAverageLocation(avg)
      console.log(`📍 [useLocation] Stopped sampling. Average:`, avg)
    } else {
      console.log('📍 [useLocation] Stopped sampling (no samples collected)')
    }
  }, [samples])

  // Single-shot location capture (for "Use Current Location" buttons)
  const getCurrentLocation = useCallback(async (): Promise<{ lat: number; lng: number } | null> => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported')
      return null
    }

    setError(null)

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          console.log(`📍 [useLocation] Current location: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`)
          resolve(location)
        },
        (err) => {
          setError(`Could not get location: ${err.message}`)
          console.error('✗ [useLocation] getCurrentLocation failed:', err)
          resolve(null)
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      )
    })
  }, [])

  /**
   * Capture initial location on page load (non-blocking, state-tracked).
   * Stores result in `initialLocation` so the setup step can show it reactively.
   */
  const captureInitialLocation = useCallback(() => {
    if (!navigator.geolocation || isLocating || initialLocation) return

    setIsLocating(true)
    setError(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }
        setInitialLocation(loc)
        setPermissionStatus('granted')
        setIsLocating(false)
        console.log(`📍 [useLocation] Initial location: ${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)} (±${Math.round(loc.accuracy)}m)`)
      },
      (err) => {
        setIsLocating(false)
        // Don't set permissionStatus to 'denied' here — user may allow later during scan
        console.warn('⚠️ [useLocation] Initial location failed:', err.message)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }, [isLocating, initialLocation])

  // Clear samples
  const clearSamples = useCallback(() => {
    setSamples([])
    setAverageLocation(null)
    setError(null)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    samples,
    averageLocation,
    permissionStatus,
    error,
    isActive,
    initialLocation,
    isLocating,
    startSampling,
    stopSampling,
    requestPermission,
    getCurrentLocation,
    captureInitialLocation,
    clearSamples,
  }
}
